import { IValidationProblemDetails } from '@kcftech/prometheus-client';

export interface IGeneralDetailError {
    Detail: string;
    Status: number;
}

/**
 * Returns true if `item` is indexable by `field`.
 */
export const isIndexable = (
    item: unknown,
    field: string,
): item is Record<string, unknown> =>
    typeof item === 'object' && item !== null && field in item;

export const isGeneralDetailError = (error: unknown): error is IGeneralDetailError =>
    typeof error === 'object' &&
    error !== null &&
    'Detail' in error &&
    isIndexable(error, 'Detail') &&
    typeof error.Detail === 'string' &&
    'Status' in error &&
    isIndexable(error, 'Status') &&
    typeof error.Status === 'number';

export const isValidationProblemDetails = (
    error: unknown,
): error is IValidationProblemDetails =>
    typeof error === 'object' &&
    error !== null &&
    'Detail' in error &&
    isIndexable(error, 'Detail') &&
    'Status' in error &&
    isIndexable(error, 'Status') &&
    typeof error.Status === 'number';

export const extractErrorString = (error: unknown): string | undefined => {
    if (typeof error !== 'object' || !error) {
        if (typeof error === 'string') {
            // Someone made our job easy
            return error;
        }
        // Don't parse errors in unrecognized format
        return undefined;
    }
    if ('Status' in error) {
        const statusNum = Number(error.Status);
        if (Number.isNaN(statusNum)) {
            // Don't parse error if there was a `Status` prop, but it wasn't a
            // number (or wasn't convertible to a number)
            return undefined;
        }
        if (statusNum < 400 || statusNum >= 500) {
            // Don't parse non-client errors (errors without a 4xx `Status`)
            return undefined;
        }
    } else {
        // Don't parse errors without a `Status`
        return undefined;
    }

    if ('Detail' in error && typeof error.Detail === 'string') {
        return error.Detail;
    }

    if ('Title' in error) {
        if (
            error.Title === 'One or more validation errors occurred.' &&
            'Errors' in error &&
            typeof error.Errors === 'object'
        ) {
            return Object.values(error.Errors as unknown as Record<string, string>)
                .flatMap((arr) => arr)
                .join('\n');
        }

        if (typeof error.Title === 'string') {
            return error.Title;
        }
    }

    return undefined;
};

export function getErrorStatus(error: unknown): number | undefined {
    if (
        !!error &&
        typeof error === 'object' &&
        'Status' in error &&
        typeof error.Status === 'number'
    ) {
        return error.Status;
    }

    return undefined;
}
